import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import MainApi from "../../../../../../services/mainApi.service";
import { memo, useCallback, useMemo, useState } from "react";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { OfferClassification } from "./OfferClassificationForm/OfferClassification";
import { ITicketAutomationReceivedRuleOfferClassif } from "../../../../../../models/ticket-aut-rec-rule-offer-classif";

const TICKET_AUTOMATION_RECEIVED_RULE_OFFER_CLASSIF_ROUTE =
  process.env.REACT_APP_AUTOMATIONS +
  "/api/v1/TicketAutomationReceivedRuleOfferClassif/";
const ticketAutomationReceivedRuleOfferClassifService =
  new MainApi<ITicketAutomationReceivedRuleOfferClassif>(
    TICKET_AUTOMATION_RECEIVED_RULE_OFFER_CLASSIF_ROUTE,
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutomationReceivedRuleOfferClassif.";

const OfferClassificationPage = () => {
  const { t } = useTranslation();

  const { id: ticketAutomationReceivedRuleId } = useParams();

  const [classificationForm, setClassificationForm] = useState(false);

  const [showButton, setShowButton] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<ITicketAutomationReceivedRuleOfferClassif>[] = [
    {
      field: "ticketAutomationReceivedRuleOfferClassifClassifications",
      headerName: t(translationPath + "classifications") || "Classifications",
      renderCell: ({
        row: { ticketAutomationReceivedRuleOfferClassifClassifications },
      }) => {
        if (
          !ticketAutomationReceivedRuleOfferClassifClassifications ||
          ticketAutomationReceivedRuleOfferClassifClassifications.length === 0
        ) {
          return "-";
        }

        const classifications =
          ticketAutomationReceivedRuleOfferClassifClassifications
            .map((c) => c.externalName)
            .join("; ");
        return (
          <OverflowTooltip
            text={classifications}
            tooltip={classifications}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "defaultClassifExternalName",
      headerName:
        t(translationPath + "default_classification") ||
        "Default Classification",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    setClassificationForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<ITicketAutomationReceivedRuleOfferClassif>,
  ) => {
    if (!params.row) return;
    setClassificationForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setSelectedId(undefined);
    setClassificationForm(false);
  }, []);

  const props = useMemo(() => {
    return {
      TicketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
    };
  }, [ticketAutomationReceivedRuleId]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {classificationForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <OfferClassification
            id={selectedId}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!classificationForm && (
        <Box height={600}>
          <Table<ITicketAutomationReceivedRuleOfferClassif>
            showDefaultMacroFilters={false}
            service={ticketAutomationReceivedRuleOfferClassifService}
            columns={columns}
            title={""}
            onFetchData={(items) => {
              setShowButton(items.length === 0);
            }}
            onFetchDelete={() => {
              setShowButton(true);
            }}
            showButton={showButton}
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputAutoFocus={false}
            defaultPageSize={20}
            serviceProps={props}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={""}
            addButtonLabel={
              t(translationPath + "add_offer_classification") ||
              "Add Offer Classification"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(OfferClassificationPage);
